import { ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AdminService } from '../../../services/admin.service';
import { ModelsTreeComponent } from '../../models-tree/models-tree.component';
import { ServerResponse } from '../../../interfaces/ServerResponse';
import { ViewsTree } from '../../../interfaces/ViewsTree';
import { BeNeo4jService } from '../../../services/be-neo4j.service';
import { DataService } from '../../../interfaces/DataService';
import { SharedService } from '../../../services/shared.service';
import { SimpleInputDialogComponent } from '../simple-input-dialog/simple-input-dialog.component';
import { NotifyDialogService } from '../../../services/notify-dialog.service';
import { ViewFromServer } from '../../../interfaces/ViewFromServer';
import { SelectViewDialogComponent } from '../select-view-dialog/select-view-dialog.component';

export interface SeletedDataService {
  dataservice: DataService
}

@Component({
  selector: 'app-tree-models-dialog',
  standalone: true,
  imports: [
    MatProgressSpinnerModule,
    MatMenuModule,
    MatExpansionModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    ModelsTreeComponent,
    MatDialogModule
  ],
  templateUrl: './tree-models-dialog.component.html',
  styleUrl: './tree-models-dialog.component.scss'
})
export class TreeModelsDialogComponent implements OnInit{

  constructor(private notify: NotifyDialogService, private beNeo4jService: BeNeo4jService, private cdr: ChangeDetectorRef){}

  ngOnInit(): void {
    this.getModels();
  }

  @ViewChild(ModelsTreeComponent, {static: false}) modelTree!: ModelsTreeComponent;

  private readonly dialogRef = inject(MatDialogRef<TreeModelsDialogComponent>, { optional: true });
  data = inject<SeletedDataService>(MAT_DIALOG_DATA);

  readonly dialog = inject(MatDialog);

  isLoading: boolean = false;

  getModels() {
    this.isLoading = true;
    this.beNeo4jService.getViewsStruct(this.data.dataservice.data_service_id).subscribe({
      next: (res: ServerResponse<ViewsTree>) => {
        this.isLoading = false;
        this.cdr.detectChanges();
        let tree = res.result;
        tree.forEach(branch => {
          if(branch.children) this.assignTreeFatherValue(branch.children, branch);
        });
        this.modelTree.ds_models = tree;
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  assignTreeFatherValue(tree: ViewsTree[], father: ViewsTree){
    tree.map((branch) => {
      branch.fatherUUID = father.__EDT_UUID__;
      if(branch.children) this.assignTreeFatherValue(branch.children, branch);
      return branch;
    });
  }

  addRootBranch(){
    this.showBasicInput((value: string)=>{
      this.isLoading = true;
      this.beNeo4jService.addNestedNodeViewTree(value, this.data.dataservice.data_service_id).subscribe({
        next: (res: any)=>{
          this.isLoading = false;
          this.getModels();
        },
        error: ()=>{
          this.isLoading = false;
        }
      });
    });
  }

  updateBranch(){
    let branch = this.modelTree.currentBranchSelected;
    if(!branch) return;
    this.showBasicInput((value: string)=>{
      this.isLoading = true;
      this.beNeo4jService.updateNestedNodeViewTree(value, branch.__EDT_UUID__, this.data.dataservice.data_service_id).subscribe({
        next: (res: any)=>{
          this.isLoading = false;
          this.getModels();
        },
        error: ()=>{
          this.isLoading = false;
        }
      });
    });
  }

  addNestedBranch(){
    let branch = this.modelTree.currentBranchSelected;
    if(!branch) return;
    this.showBasicInput((value: string)=>{
      this.isLoading = true;
      this.beNeo4jService.addNestedNodeViewTree(value, this.data.dataservice.data_service_id, branch.__EDT_UUID__).subscribe({
        next: (res: any)=>{
          this.isLoading = false;
          this.getModels();
        },
        error: ()=>{
          this.isLoading = false;
        }
      });
    });
  }

  deleteBranch(){
    let branch = this.modelTree.currentBranchSelected;
    if(!branch) return;
    this.notify.openDialog("Delete", "Do you really want to delete the branch?", true, ()=>{
      this.isLoading = true;
      this.beNeo4jService.removeNestedNodeViewTree(branch.__EDT_UUID__, this.data.dataservice.data_service_id).subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.getModels();
        },
        error: () => {
          this.isLoading = false;
        }
      });
    });
  }

  linkViewToBranch(){
    let branch = this.modelTree.currentBranchSelected;
    if(!branch) return;
    this.isLoading = true;
    this.beNeo4jService.linkView_ViewStructure(branch.__EDT_UUID__, "", this.data.dataservice.data_service_id).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.getModels();
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  unlinkViewToBranch(){
    let branch = this.modelTree.currentBranchSelected;
    if(!branch) return;
    this.isLoading = true;
    this.beNeo4jService.removeLinkView_ViewStructure(branch.fatherUUID, branch.viewUUID, this.data.dataservice.data_service_id).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        this.getModels();
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  showBasicInput(callback: Function){
    const ref = this.dialog.open(SimpleInputDialogComponent, {
      data: {},
    });
    ref.afterClosed().subscribe({
      next: (value: string)=>{
        if(value){
          callback(value);
        }
      }
    });
  }

  showViewsMenu(){
    let branch = this.modelTree.currentBranchSelected;
    if(!branch) return;
    const ref = this.dialog.open(SelectViewDialogComponent, {
      data: {},
    });
    ref.afterClosed().subscribe({
      next: (value: ViewFromServer)=>{
        if(value){
          this.beNeo4jService.linkView_ViewStructure(branch.__EDT_UUID__, value.viewUUID, this.data.dataservice.data_service_id).subscribe({
            next: (res: any) => {
              this.isLoading = false;
              this.getModels();
            },
            error: () => {
              this.isLoading = false;
            }
          });
        }
      }
    });
  }

  onCancel = ()=>{
    this.dialogRef?.close();
  }
}
